import ArtCircle from '../components/layout/ArtCircle/ArtCircle';
import ExternalLink from '../components/layout/ExternalLink/ExternalLink';
import styles from './privacy-policy.module.scss';
import SpellbookHead from '../components/SpellbookHead/SpellbookHead';
import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <SpellbookHead
        title="Commander Spellbook: Privacy Policy"
        description="Privacy policy for the use of Commander Spellbook."
      />
      <div className={`static-page ${styles.privacyPolicyContainer}`}>
        <ArtCircle cardName="Bruvac the Grandiloquent" className="m-auto md:block hidden" />
        <h1 className="heading-title text-center">Privacy Policy</h1>

        <p>
          We consider the privacy of our visitors to be extremely important. This privacy policy document describes in
          detail the types of personal information is collected and recorded by Commander Spellbook and how we use it.
        </p>

        <h2 className="heading-subtitle">Log Files</h2>

        <p>
          Like many other websites, we makes use of log files. These files merely logs visitors to the site - usually a
          standard procedure for hosting companies and a part of hosting services's analytics. The information inside
          the log files includes internet protocol (IP) addresses, browser type, Internet Service Provider (ISP),
          date/time stamp, referring/exit pages, and possibly the number of clicks. This information is used to analyze
          trends, administer the site, track user's movement around the site, and gather demographic information. IP
          addresses, and other such information are not linked to any information that is personally identifiable.
        </p>

        <h2 className="heading-subtitle">Cookies and Web Beacon</h2>

        <p>
          We use cookies to store information about visitors' preferences, to record user-specific information on which
          pages the site visitor accesses or visits, and to personalize or customize our web page content based upon
          visitors' browser type or other information that the visitor sends via their browser.
        </p>

        <h2 className="heading-subtitle">Third Party Privacy Policies</h2>

        <p>
          You should consult the respective privacy policies of these third-party ad servers for more detailed
          information on their practices as well as for instructions about how to opt-out of certain practices. Our
          privacy policy does not apply to, and we cannot control the activities of, such other advertisers or web
          sites. You may find a comprehensive listing of these privacy policies and their links here:&nbsp;
          <ExternalLink href="https://www.privacypolicyonline.com/privacy-policy-links/">
            Privacy Policy Links
          </ExternalLink>
        </p>

        <h2 className="heading-subtitle">Children's Information</h2>

        <p>
          We believe it is important to provide added protection for children online. We encourage parents and guardians
          to spend time online with their children to observe, participate in and/or monitor and guide their online
          activity. We do not knowingly collect any personally identifiable information from children under the age of
          13. If a parent or guardian believes that has in its database the personally-identifiable information of a
          child under the age of 13, please contact us immediately (using the contact in the first paragraph) and we
          will use our best efforts to promptly remove such information from our records.
        </p>

        <h2 className="heading-subtitle">Online Privacy Policy Only</h2>

        <p>
          This privacy policy applies only to our online activities and is valid for visitors to our website and
          regarding information shared and/or collected there. This policy does not apply to any information collected
          offline or via channels other than this website.
        </p>

        <hr />

        <h2 className="heading-subtitle">For California Residents: CCPA Information</h2>

        <p>
          This Privacy Notice for California Residents supplements the information contained elsewhere in this Privacy
          Notice and applies solely to all visitors, users, and others who reside in the State of California
          (“consumers” or “you”). We adopt this notice to comply with the California Consumer Privacy Act of 2018 (CCPA)
          and any terms defined in the CCPA have the same meaning when used in this notice.
        </p>

        <h3>Rights and Choices</h3>

        <p>
          The CCPA provides consumers located in the state of California with certain rights regarding their personal
          information and data. The following section describes those rights and explains how to exercise them:
        </p>

        <h3>Access to Specific Information and Data Portability Rights</h3>

        <p>
          You have the right to request that the company disclose certain information to you about our collection and
          use of your personal information over the past 12 months. Once we receive and confirm your verifiable consumer
          request (as described in the section “Exercising Access, Data Portability, and Deletion Rights”), we will
          disclose to you:
        </p>
        <ul>
          <li>The categories of personal information we collected about you.</li>
          <li>The categories of sources for the personal information we collected about you.</li>
          <li>Our business or commercial purpose for collecting or selling that personal information.</li>
          <li>The categories of third parties with whom we share that personal information.</li>
          <li>
            The specific pieces of personal information we collected about you (also called data portability request)
          </li>
          <li>
            If we sold or disclose your personal information for a business purpose, two separate lists disclosing:
            <ul>
              <li>
                Sales, identifying the personal information categories that each category of recipient purchased, and
              </li>
              <li>
                Disclosures for a business purpose, identifying the personal information categories that each category
                of recipient obtained
              </li>
            </ul>
          </li>
        </ul>

        <h3>Non-Discrimination</h3>

        <p>
          We will not discriminate against you for exercising any of your CCPA rights. Unless permitted by the CCPA, we
          will not:
        </p>
        <ul>
          <li>Deny you goods or services.</li>
          <li>
            Charge you different prices or rates for goods or services, including through granting discounts or imposing
            penalties.
          </li>
          <li>Provide you a different level or quality of goods or services.</li>
          <li>
            Suggest that you may receive a different price or rate for goods or services or a different level of quality
            of goods or services.
          </li>
        </ul>
        <p>
          Any CCPA-permitted financial incentive we offer will reasonably relate to your value and contain written terms
          that describe the program’s material aspects.
        </p>

        <h3>Exercising Access, Data Portability, and Deletion Rights</h3>

        <p>
          To exercise the access, data portability, and deletion rights described above, please submit a verifiable
          consumer request to us by
          <ExternalLink href="mailto:edhrec@edhrec.com">contacting us</ExternalLink>.
        </p>
        <p>
          Only you, or a person registered with the California Secretary of State that you authorize to act on your
          behalf, may make a verifiable consumer request related to your personal information. You may also make a
          verifiable consumer request on behalf of your minor child.
        </p>
        <p>
          You may only make a verifiable consumer request for access of data portability twice within a 12-month period.
          The verifiable consumer request must:
        </p>
        <ul>
          <li>
            Provide sufficient information that allows us to reasonably verify you are the person about whom we
            collected personal information or an authorized representative.
          </li>
          <li>
            Describe your request with sufficient detail that allows us to properly understand, evaluate, and respond to
            it.
          </li>
        </ul>
        <p>
          We cannot respond to your request or provide you with personal information if we cannot verify your identity
          or authority to make the request and confirm the personal information relates to you.
        </p>
        <p>
          Making a verifiable consumer request does not require you to create an account with us. We will only use
          personal information provided in a verifiable consumer request to verify the requestor’s identity or authority
          to make the request.
        </p>

        <h3>Information We Collect</h3>

        <p>
          Our websites, emails (with your consent, where required by law), and other products, services and platforms
          collect information that identifies, relates to, describes, references, is capable of being associated with,
          or could reasonably be linked, directly or indirectly, with a particular consumer or device (“personal
          information”). In particular, our websites, apps, emails, and other products, services and platforms may have
          collected the following categories of personal information from its consumers within the last twelve (12)
          months:
        </p>

        <table className="table-auto my-4">
          <tbody>
            <tr>
              <th className="text-left">Category</th>
              <th>Collected</th>
            </tr>
            <tr>
              <td>A. Identifiers</td>
              <td>YES</td>
            </tr>
            <tr>
              <td>
                B. Personal information categories listed in the California Customer Records statute (Cal. Civ. Code §
                1798.80(e))
              </td>
              <td>YES</td>
            </tr>
            <tr>
              <td>C. Protected classification characteristics under California or federal law</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>D. Commercial information.</td>
              <td>YES</td>
            </tr>
            <tr>
              <td>E. Biometric information.</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>F. Internet or other similar network activity.</td>
              <td>YES</td>
            </tr>
            <tr>
              <td>G. Geolocation data.</td>
              <td>YES</td>
            </tr>
            <tr>
              <td>H. Sensory data.</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>I. Professional or employment-related information.</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>J. Inferences drawn from other personal information.</td>
              <td>YES</td>
            </tr>
          </tbody>
        </table>

        <p className="my-4">Personal information does not include:</p>

        <ul>
          <li>Publicly available information from government records.</li>
          <li>Deidentified or aggregated consumer information.</li>
          <li>
            Information excluded from the CCPA’s scope, like:
            <ul className="ml-8">
              <li>
                health or medical information covered by the Health Insurance Portability and Accountability Act of 1996
                (HIPAA) and the California Confidentiality of Medical Information Act (CMIA) or clinical trial data;
              </li>
              <li>
                personal information covered by certain sector-specific privacy laws, including the Fair Credit
                Reporting Act (FRCA), the Gramm-Leach-Bliley Act (GLBA) or California Financial Information Privacy Act
                (FIPA), and the Driver’s Privacy.
              </li>
            </ul>
          </li>
        </ul>

        <h3>Use of Personal Information</h3>

        <p>
          We may use or disclose the personal information we collect for one or more of the following business purposes:
        </p>

        <ul>
          <li>
            To fulfill or meet the reason you provided the information. For example, if you share your name and contact
            information to request a newsletter or ask a question about our products or services, we will use that
            personal information to respond to your inquiry. If you provide your personal information to purchase a
            product or service, we or our third-party service providers will use that information to process your
            payment and facilitate delivery. We may also save your information to facilitate new product or service
            orders and requests.
          </li>
          <li>
            To provide, support, personalize, and develop our websites, emails, and other products, services and
            platforms.
          </li>
          <li>To create, maintain, customize, and secure your account with us.</li>
          <li>To process your requests, purchases, transactions, and payments and prevent transactional fraud.</li>
          <li>
            To provide you with support and to respond to your inquiries, including investigating and addressing your
            concerns and monitoring and improving our responses.
          </li>
          <li>
            To personalize your website, apps, emails, or other product, service or platform experience and to deliver
            content and product and service offerings relevant to your interests, including targeted offers and ads
            through our websites, apps, emails, and other products, services and platforms.
          </li>
          <li>
            To help maintain the safety, security, and integrity of our websites, apps, emails, and other products,
            services and platforms, databases and other technology assets, and business.
          </li>
          <li>
            For testing, research, analysis, and product development, including to develop and improve our websites,
            apps, emails, and other products, services and platforms.
          </li>
          <li>
            To respond to law enforcement requests and as required by applicable law, court order, or governmental
            regulations.
          </li>
          <li>As described to you when collecting your personal information or as otherwise set forth in the CCPA.</li>
          <li>
            To evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or
            transfer of some or all of the Company’s assets, whether as a going concern or as part of bankruptcy,
            liquidation, or similar proceeding, in which personal information held by the Company about our Website
            users is among the assets transferred.
          </li>
        </ul>
        <p>
          The Company will not collect additional categories of personal information or use the personal information we
          collected for materially different, unrelated, or incompatible purposes without providing you notice.
        </p>

        <h3>Sharing Personal Information</h3>

        <p>
          The Company may disclose your personal information to a third-party for a business purpose or sell your
          personal information, subject to your right to opt-out of those sales (see ‘Sales of Personal Information’
          below). When we disclose personal information for a business purpose, we enter a contract that describes the
          purpose and requires the recipient to both keep that personal information confidential and not use it for any
          purpose except performing the contract. The CCPA prohibits third parties who purchase the personal information
          we hold from reselling it unless you have received explicit notice and an opportunity to opt-out of further
          sales (see ‘Sales of Personal Information’ below).
        </p>
        <p>We may share your personal information with the following categories of third parties:</p>

        <ul>
          <li>Subsidiaries and affiliates.</li>
          <li>Contractors and service providers.</li>
          <li>Data aggregators.</li>
          <li>Third parties with whom we partner to offer products and services to you.</li>
        </ul>

        <h3>Disclosures of Personal Information for a Business Purpose</h3>

        <p>
          In the preceding twelve (12) months, the Company has disclosed the following categories of personal
          information for a business purpose:
        </p>

        <ul>
          <li>[Category A: Identifiers.]</li>
          <li>[Category B: California Customer Records personal information categories.]</li>
          <li>[Category F: Internet or other similar network activity.]</li>
          <li>[Category G: Geolocation Data.]</li>
          <li>[Category K: Inferences drawn from other personal information.]</li>
        </ul>

        <h3>Sales of Personal Information</h3>

        <p>
          In the preceding twelve (12) months, the company has sold the following categories of personal information
          collected through our ad-supported services:
        </p>
        <ul>
          <li>[Category A: Identifiers.]</li>
          <li>[Category F: Internet or other similar network activity.]</li>
          <li>[Category G: Geolocation Data.]</li>
          <li>[Category K: Inferences drawn from other personal information.]</li>
        </ul>
        <p>
          The company and our advertising partners collect the personal information identified above (such as the
          cookies stored on your browser, the advertising identifier on your mobile device, or the IP address of your
          device) when you visit our websites, apps, and other products, services and platforms, or open our emails. We,
          and our partners, use this information to tailor and deliver ads to you on our websites, apps, emails, and
          other products, services and platforms, or to help tailor ads to you when you visit others’ sites (or use
          others’ apps). To tailor ads that may be more relevant to you, we and/or our partners may share the
          information we collect with third parties.
        </p>

        <h2 className="heading-subtitle">Consent</h2>

        <p>By using our website, you hereby consent to our privacy policy and agree to its terms.</p>
      </div>
    </>
  );
};

export default PrivacyPolicy;
